/* eslint-disable max-lines-per-function */
import React from 'react';
import PropTypes from 'prop-types';
import FormDialog from '../../../src/Components/Modal/modal';
import SearchMultiSelect from '../../../src/Components/MultiSelectDropdown/multiSelectDropdown';
import UseAxios from '../../../src/Api/http/http';
import { useSnackbar } from '../../Components/Snackbar/snackBarProvider';
import './mapRoles.scss';
import {
  MAP_EDIT_USER_ROLE,
  CANCEL_TEXT,
  SAVE,
  SELECT,
  UPDATE,
  MAP_EDIT_USER_RESPONSE_MSG,
} from '../../../src/Constants/strings';
import { userEditMapRole } from '../../Api/request/roleMap';
import useRoles from './CustomHooks/useRoles';
import useMapForm from './CustomHooks/useMapForm';

const MapForm = ({
  modalOpen,
  handleClose,
  setRows,
  isEditMode,
  isRemoveMode,
  selectedData,
  handleRowSelectionChange,
  setActionLoading,
}) => {
  const { rolesOption } = useRoles();
  const {
    roleName,
    setRoleName,
    roleError,
    setRoleError,
    resetValues,
    disabledRoleOptions,
    removeRoleOptions,
  } = useMapForm(isEditMode, selectedData, rolesOption, isRemoveMode);
  const { snackbarOpen } = useSnackbar();

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const updateRows = (prevRows, activeRoles) => {
    return prevRows.map((row) => {
      const selectedItem = selectedData.find(
        (data) => data.employeeId === row.employeeId,
      );
      if (selectedItem) {
        if (isRemoveMode) {
          // Remove mode: activeRoles are the remaining kept roles
          const keptNames = activeRoles.map((roleObj) => roleObj.name);
          const existingRoleNames = row.roleName && row.roleName !== 'NA'
            ? row.roleName
              .split(',')
              .map((roleItem) => roleItem.trim())
              .filter(Boolean)
            : [];
          const remainingRoleNames = existingRoleNames.filter((name) => keptNames.includes(name));
          const roleNamesString = remainingRoleNames.join(', ');
          return {
            ...row,
            roleName: roleNamesString !== '' ? roleNamesString : 'NA',
          };
        }

        const existingRoleNames = row.roleName && row.roleName !== 'NA'
          ? row.roleName
            .split(',')
            .map((roleItem) => roleItem.trim())
            .filter(Boolean)
          : [];
        const mergedSet = new Set([
          ...existingRoleNames,
          ...activeRoles.map((roleObj) => roleObj.name),
        ]);
        const roleNamesString = Array.from(mergedSet).join(', ');
        return {
          ...row,
          roleName: roleNamesString !== '' ? roleNamesString : 'NA',
        };
      }
      return row;
    });
  };

  const handleResponse = (responseData) => {
    const errorResponse = responseData?.data?.response;

    if (responseData?.data?.success === true) {
      setRows((prevRows) => updateRows(prevRows, roleName));
      snackbarOpen(
        isRemoveMode
          ? 'Roles removed successfully'
          : isEditMode
            ? MAP_EDIT_USER_RESPONSE_MSG.ROLE_UPDATED_SUCCESS
            : MAP_EDIT_USER_RESPONSE_MSG.MAP_ADDED_SUCCESS,
        'success',
      );
      handleCloseWithReset();
      handleRowSelectionChange([]);
    } else if (errorResponse?.data?.success === false) {
      const errorRoles = errorResponse.data.error.message
        .match(/: (.*)/)?.[1]
        ?.split(',')
        ?.map((role) => role.trim()) || [];

      const validRoles = roleName.filter((role) => !errorRoles.includes(role.name));
      snackbarOpen(errorResponse.data.error.message, 'warning', 7000);
      setRows((prevRows) => updateRows(prevRows, validRoles));
      handleCloseWithReset();
      if (validRoles.length > 0) {
        handleRowSelectionChange([]);
      }
    } else {
      snackbarOpen(MAP_EDIT_USER_RESPONSE_MSG.OOPS_TRY_AGAIN, 'warning');
    }
  };

  const handleSaveOrUpdate = async () => {
    try {
      if (setActionLoading) setActionLoading(true);
      const token = localStorage.getItem('userRolesToken');
      const selectedRoleIds = roleName.map((role) => role.id);

      const editPayload = {
        users: selectedData
          .filter((user) => user.emailId && typeof user.emailId === 'string' && user.emailId.trim() !== '')
          .map((user) => {
            const parsedId = Number(user.roleMapId);
            const hasValidId = !isNaN(parsedId) && parsedId > 0;

            let finalRoleIds = [];
            if (isEditMode) {
              // Edit mode: explicit exact replacement for single user
              finalRoleIds = selectedRoleIds;
            } else if (isRemoveMode) {
              // Remove mode: roleName contains the KEPT roles (roles that remain checked).
              // So we keep any existing role whose name is still included in roleName.
              const keptRoleNames = roleName.map((roleObj) => roleObj.name);
              const existingRoleNames = user.roleName && user.roleName !== 'NA'
                ? user.roleName
                  .split(',')
                  .map((roleItem) => roleItem.trim())
                  .filter(Boolean)
                : [];
              const remainingRoleNames = existingRoleNames.filter((name) => keptRoleNames.includes(name));
              finalRoleIds = rolesOption
                .filter((opt) => remainingRoleNames.includes(opt.name))
                .map((opt) => opt.id);
            } else {
              // Map mode (Add roles): merge existing roles with newly selected roles
              const existingRoleNames = user.roleName && user.roleName !== 'NA'
                ? user.roleName
                  .split(',')
                  .map((roleItem) => roleItem.trim())
                  .filter(Boolean)
                : [];
              const existingRoleIds = rolesOption
                .filter((opt) => existingRoleNames.includes(opt.name))
                .map((opt) => opt.id);
              finalRoleIds = Array.from(new Set([...existingRoleIds, ...selectedRoleIds]));
            }

            return {
              ...(hasValidId ? { id: parsedId } : {}),
              emailId: user.emailId.trim(),
              name: user.fullName,
              roleId: finalRoleIds,
            };
          }),
      };

      const response = await UseAxios(userEditMapRole(editPayload, token));
      handleResponse(response);
    } catch (error) {
      snackbarOpen(MAP_EDIT_USER_RESPONSE_MSG.OOPS_TRY_AGAIN, 'warning');
      console.error('Error processing role mapping', error);
    } finally {
      if (setActionLoading) setActionLoading(false);
    }
  };

  return (
    <div>
      <FormDialog
        title={
          isRemoveMode
            ? 'Remove Role'
            : isEditMode
              ? MAP_EDIT_USER_ROLE.EDIT_ROLE
              : MAP_EDIT_USER_ROLE.MAP_ROLE
        }
        open={modalOpen}
        button1Action={handleCloseWithReset}
        button2Action={handleSaveOrUpdate}
        dialogPaperCss="map-dialog-paper"
        dialogCss="map-dialog"
        dialogBodycontent={
          <div className="map-dialog-body">
            <div className="map-multi-select">
              <SearchMultiSelect
                options={isRemoveMode ? removeRoleOptions : rolesOption}
                disabledOptions={disabledRoleOptions}
                label={isRemoveMode ? 'Assigned Roles' : MAP_EDIT_USER_ROLE.SELECT_ROLE}
                placeholder={SELECT}
                value={roleName}
                onChange={(event) => {
                  setRoleName(event.target.value);
                  setRoleError('');
                }}
                id="map-vertical-dropdown"
              />
              {roleError && (
                <div className="map-error">
                  <small>{roleError}</small>
                </div>
              )}
            </div>
          </div>
        }
        button1={CANCEL_TEXT}
        button2={isRemoveMode ? 'Remove' : isEditMode ? UPDATE : SAVE}
        cancelButtonId="map-cancel-btn"
        submitButtonId="map-submit-btn"
      />
    </div>
  );
};

MapForm.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  setRows: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  isRemoveMode: PropTypes.bool,
  selectedData: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string,
      employeeId: PropTypes.string,
      roleMapId: PropTypes.string,
      fullName: PropTypes.string,
      emailId: PropTypes.string,
    }),
  ),
  handleRowSelectionChange: PropTypes.func,
  setActionLoading: PropTypes.func,
};

MapForm.defaultProps = {
  isEditMode: false,
  isRemoveMode: false,
  selectedData: [],
};

export default MapForm;
